
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEvents } from "@/contexts/EventsContext";
import { Calendar, MapPin, Users, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { EventDetails } from "@/components/events/EventDetails";

export const Applications = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { events } = useEvents();
  const navigate = useNavigate();

  // تصفية الأحداث لإظهار فقط تلك في فئة التطبيقات
  const applicationEvents = events.filter(event => event.category === "Applications");

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? "rtl" : "ltr"}`}>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            {isRTL ? (
              <>
                العودة للرئيسية
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </>
            )}
          </Button>
          <h1 className="text-3xl font-bold text-gray-800">
            {isRTL ? "التطبيقات" : "Applications"}
          </h1>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {applicationEvents.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="h-48 relative overflow-hidden group">
                <img
                  src={event.image}
                  alt={event.title[isRTL ? "ar" : "en"]}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#0056b3] text-sm mb-2">
                  <Calendar className="w-4 h-4" />
                  {event.date[isRTL ? "ar" : "en"]}
                </div>
                {event.startTime && event.endTime && (
                  <div className="flex items-center gap-2 text-[#0056b3] text-sm mb-2">
                    <Clock className="w-4 h-4" />
                    {event.startTime[isRTL ? "ar" : "en"]} - {event.endTime[isRTL ? "ar" : "en"]}
                  </div>
                )}
                <h3 className="font-display text-xl font-semibold mb-2 text-gray-800 hover:text-[#0056b3] transition-colors">
                  {event.title[isRTL ? "ar" : "en"]}
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.location[isRTL ? "ar" : "en"]}
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {event.attendees} {isRTL ? "مشارك" : "attendees"}
                  </div>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full border-[#0056b3] text-[#0056b3] hover:bg-[#0056b3] hover:text-white"
                    >
                      {isRTL ? "تفاصيل الفعالية" : "Event Details"}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>
                        {event.title[isRTL ? "ar" : "en"]}
                      </DialogTitle>
                    </DialogHeader>
                    <EventDetails event={event} isRTL={isRTL} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Applications;
